import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditLogAPI } from '../utils/api';
import {
  ClockIcon,
  FunnelIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { formatDateTime } from '../utils/formatDateTime';

const actionColors = {
  'user.login': 'bg-blue-100 text-blue-700',
  'alert.acknowledged': 'bg-amber-100 text-amber-700',
  'alert.resolved': 'bg-emerald-100 text-emerald-700',
  'camera.post': 'bg-purple-100 text-purple-700',
  'camera.put': 'bg-purple-100 text-purple-700',
  'camera.delete': 'bg-red-100 text-red-700',
};

const entityTypeLabels = {
  CameraDetectionConfig: 'Camera Config',
  CameraDetectionProfile: 'Camera Profile',
  AIDeviceConfig: 'AI Device Settings',
  EdgeDeviceConfig: 'Edge Device Settings',
};

const entityTypeColors = {
  CameraDetectionConfig: 'bg-purple-100 text-purple-700',
  CameraDetectionProfile: 'bg-indigo-100 text-indigo-700',
  AIDeviceConfig: 'bg-cyan-100 text-cyan-700',
  EdgeDeviceConfig: 'bg-teal-100 text-teal-700',
};

// Shallow diff of two config objects — returns keys whose values differ
function diffConfigs(prev, next) {
  const changes = [];
  const keys = new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]);
  for (const key of keys) {
    const a = JSON.stringify(prev?.[key]);
    const b = JSON.stringify(next?.[key]);
    if (a !== b) changes.push({ key, from: prev?.[key], to: next?.[key] });
  }
  return changes;
}

function ConfigChangeRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  const changes = diffConfigs(log.previousConfig, log.newConfig);

  return (
    <>
      <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(e => !e)}>
        <td className="whitespace-nowrap text-sm text-slate-600">
          {formatDateTime(log.changedAt)}
        </td>
        <td>
          <span className={clsx(
            'px-2 py-1 rounded-md text-xs font-semibold',
            entityTypeColors[log.entityType] || 'bg-slate-100 text-slate-600'
          )}>
            {entityTypeLabels[log.entityType] || log.entityType}
          </span>
        </td>
        <td className="text-sm text-slate-700 font-medium">{log.entityName}</td>
        <td className="text-sm text-slate-600">{log.changedByName || <span className="text-slate-400">system</span>}</td>
        <td className="text-sm text-slate-500 max-w-xs truncate">{log.notes || '-'}</td>
        <td className="text-sm text-slate-500">
          <span className="flex items-center gap-1">
            {expanded ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            {changes.length} field{changes.length !== 1 ? 's' : ''}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-slate-50 px-6 py-3">
            {changes.length === 0 ? (
              <p className="text-sm text-slate-500">No field-level differences detected</p>
            ) : (
              <div className="space-y-2">
                {changes.map(c => (
                  <div key={c.key} className="text-xs font-mono">
                    <span className="font-semibold text-slate-700">{c.key}:</span>{' '}
                    <span className="text-red-600 line-through break-all">
                      {c.from === undefined ? '(not set)' : JSON.stringify(c.from)}
                    </span>
                    {' → '}
                    <span className="text-emerald-700 break-all">
                      {c.to === undefined ? '(removed)' : JSON.stringify(c.to)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function ConfigChangesTab() {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['config-changes', page, entityType],
    queryFn: () => auditLogAPI.getConfigChanges({ page, limit: 50, ...(entityType ? { entityType } : {}) }),
  });

  const logs = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  return (
    <>
      <div className="glass-card p-4 mb-6 flex flex-wrap items-center gap-4">
        <FunnelIcon className="w-5 h-5 text-slate-400" />
        <select
          value={entityType}
          onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
          className="input py-2 px-3 text-sm min-w-[220px]"
          aria-label="Filter by config type"
        >
          <option value="">All Config Types</option>
          {Object.entries(entityTypeLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <button onClick={() => refetch()} className="btn btn-secondary btn-sm flex items-center gap-2 ml-auto">
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="glass-card overflow-hidden">
        <table className="glass-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Type</th>
              <th>Camera / Device</th>
              <th>Changed By</th>
              <th>Notes</th>
              <th>Changes</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={6}>
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-full" />
                  </td>
                </tr>
              ))
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-slate-500">
                  <ClockIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  No config changes recorded yet
                </td>
              </tr>
            ) : (
              logs.map((log) => <ConfigChangeRow key={log.id} log={log} />)
            )}
          </tbody>
        </table>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.pages} ({pagination.total} entries)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn btn-secondary btn-sm"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ActivityTab() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: '', userId: '' });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit-log', page, filters],
    queryFn: () => auditLogAPI.getLogs({ page, limit: 50, ...filters }),
  });

  const logs = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  const { data: actionsData } = useQuery({
    queryKey: ['audit-log-actions'],
    queryFn: () => auditLogAPI.getActions(),
    staleTime: 60000,
  });

  const actions = actionsData?.data?.data || [];

  return (
    <>
      {/* Filters */}
      <div className="glass-card p-4 mb-6 flex flex-wrap items-center gap-4">
        <FunnelIcon className="w-5 h-5 text-slate-400" />
        <select
          value={filters.action}
          onChange={(e) => { setFilters(f => ({ ...f, action: e.target.value })); setPage(1); }}
          className="input py-2 px-3 text-sm min-w-[180px]"
          aria-label="Filter by action"
        >
          <option value="">All Actions</option>
          {actions.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <button onClick={() => refetch()} className="btn btn-secondary btn-sm flex items-center gap-2 ml-auto">
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Log table */}
      <div className="glass-card overflow-hidden">
        <table className="glass-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Action</th>
              <th>Entity</th>
              <th>IP Address</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={5}>
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-full" />
                  </td>
                </tr>
              ))
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-slate-500">
                  <ClockIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  No audit log entries found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td className="whitespace-nowrap text-sm text-slate-600">
                    {formatDateTime(log.createdAt)}
                  </td>
                  <td>
                    <span className={clsx(
                      'px-2 py-1 rounded-md text-xs font-semibold',
                      actionColors[log.action] || 'bg-slate-100 text-slate-600'
                    )}>
                      {log.action}
                    </span>
                  </td>
                  <td className="text-sm text-slate-600">
                    {log.entityType && (
                      <span className="text-slate-400">{log.entityType}</span>
                    )}
                    {log.entityId && (
                      <span className="text-xs text-slate-400 ml-1">({log.entityId.substring(0, 8)}...)</span>
                    )}
                  </td>
                  <td className="text-sm text-slate-500 font-mono">{log.ipAddress || '-'}</td>
                  <td className="text-sm text-slate-500 max-w-xs truncate">
                    {log.details ? JSON.stringify(log.details).substring(0, 80) : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.pages} ({pagination.total} entries)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn btn-secondary btn-sm"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function AuditLogPage() {
  const [tab, setTab] = useState('activity');

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-description">Track user activities, system events and config changes</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('activity')}
          className={clsx('btn btn-sm', tab === 'activity' ? 'btn-primary' : 'btn-secondary')}
        >
          Activity
        </button>
        <button
          onClick={() => setTab('config')}
          className={clsx('btn btn-sm', tab === 'config' ? 'btn-primary' : 'btn-secondary')}
        >
          Config Changes
        </button>
      </div>

      {tab === 'activity' ? <ActivityTab /> : <ConfigChangesTab />}
    </div>
  );
}
